/**
 * Per-drawer one-line summaries shown on the design-board overview rows.
 * Mirrors the section-properties/row-summaries.js pattern.
 *
 * Each function takes a snapshot of state (theme + selection + fonts) and
 * returns a short string for the row's right-hand label. Pure — no DOM, no
 * side effects.
 */

import { store } from '../../core/state.js';
import { getCurrentTheme, getThemeVars } from '../../theme/theme-editor.js';
import { getCurrentFont, getCurrentHeadingFont } from '../../theme/font-selector.js';
import { normalizeHex } from './_internals.js';
import { ENTRANCE_PRESETS } from '../../shared/entrance-presets.js';

/**
 * Colors row — "N in palette". Counts unique theme-var hex values that the
 * Design tab exposes as palette swatches.
 */
export function summarizeColors() {
  const theme = getCurrentTheme() || {};
  const defs = getThemeVars();
  const seen = new Set();
  for (const def of defs) {
    const hex = normalizeHex(theme[def.key] || '');
    if (hex) seen.add(hex.toLowerCase());
  }
  const n = seen.size;
  return n ? `${n} in palette` : 'Default';
}

/**
 * Fonts row — "Body / Heading". Falls back to "Default" when both inherit
 * the system stack.
 */
export function summarizeFonts() {
  const body = getCurrentFont();
  const heading = getCurrentHeadingFont();
  const bodyLabel = typeof body === 'string' ? body : body?.label || body?.family || '';
  const headingLabel =
    typeof heading === 'string' ? heading : heading?.label || heading?.family || '';
  if (!bodyLabel && !headingLabel) return 'Default';
  if (bodyLabel && headingLabel && bodyLabel !== headingLabel)
    return `${headingLabel} / ${bodyLabel}`;
  return headingLabel || bodyLabel || 'Default';
}

/**
 * Layout row — "Rounded · Lift", composed from the button radius preset and
 * the site-effects hover style. Both default-empty → "Default".
 */
export function summarizeLayout() {
  const s = store.getState();
  const radius = s?.buttonStyles?.borderRadius;
  const hover = s?.buttonStyles?.hoverEffect;
  const radiusLabels = {
    0: 'Square',
    '0.25rem': 'Slight',
    '0.5rem': 'Medium',
    '1rem': 'Round',
    '3rem': 'Pill'
  };
  const parts = [];
  if (radius) parts.push(radiusLabels[radius] || radius);
  if (hover && hover !== 'none') parts.push(hover.replace(/^./, (c) => c.toUpperCase()));
  // EA-4. The entrance lives in this drawer, so it belongs in this row's
  // summary — named by its LABEL, from the records, never a raw id.
  const preset = ENTRANCE_PRESETS.find((p) => p.id === s?.entrance?.preset);
  if (preset) parts.push(preset.label);
  else if (s?.entrance?.template === false) parts.push('No entrance');
  return parts.length ? parts.join(' · ') : 'Default';
}

export function summarizeForms() {
  const formStyles = store.getState()?.formStyles;
  if (!formStyles) return 'Default';
  const cap = (value) => (value ? value.replace(/^./, (char) => char.toUpperCase()) : '');
  const parts = [];
  if (formStyles.shape) parts.push(cap(formStyles.shape));
  if (formStyles.style) parts.push(cap(formStyles.style));
  if (formStyles.submit) parts.push(`Submit: ${cap(formStyles.submit)}`);
  return parts.length ? parts.join(' · ') : 'Default';
}
